// Picker selects for the create slide-over: PersonSelect, TeamSelect,
// RockSelect, MilestoneSelect, OpportunitySelect, plus the generic
// SelectBtn used for the quarter dropdown.
//
// Each picker is a combobox: clicking the trigger opens a panel with a
// filter input + scrollable result list. Built on native HTML +
// useEffect (no Radix); covers the same interactions as the Drillcut
// originals — type-to-filter, keyboard navigation, Esc-to-close.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ChevronDown, Check, UserRound, Users, Target, Lightbulb, Flag } from "lucide-react";
import { tpApi } from "../../api";
import { useResource } from "../../hooks";
import { initials } from "../../utils";
import { DC } from "./primitives";
import type { PlannerOwner, TeamSummary } from "../../../../../../../shared/team-planner/schemas";

interface PickerOption {
  id: string;
  label: string;
  sub?: string | null;
  icon?: ReactNode;
}

interface PickerProps {
  value: string | null;
  onChange: (id: string | null) => void;
  options: PickerOption[];
  placeholder: string;
  emptyHint?: string;
  loading?: boolean;
  triggerIcon?: ReactNode;
  triggerLabel?: ReactNode;
  disabled?: boolean;
  allowClear?: boolean;
  /** When provided, the parent owns the search query. Picker still
   *  applies its in-memory `includes` filter on the supplied options
   *  so type-ahead stays snappy while the server is fetching, but
   *  every keystroke is forwarded to the parent for re-fetching. */
  onSearchChange?: (q: string) => void;
}

function Picker({
  value,
  onChange,
  options,
  placeholder,
  emptyHint = "No matches.",
  loading,
  triggerIcon,
  triggerLabel,
  disabled,
  allowClear = true,
  onSearchChange,
}: PickerProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const filterRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(f) ||
        (o.sub ?? "").toLowerCase().includes(f),
    );
  }, [filter, options]);

  // Close on outside-click / Esc; focus the filter when opening.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey, true);
    setTimeout(() => filterRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const selected = options.find((o) => o.id === value) ?? null;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          height: 38,
          padding: "0 10px 0 12px",
          border: `1px solid ${DC.borderStrong}`,
          borderRadius: 6,
          background: disabled ? DC.surfaceMuted : DC.surface,
          color: selected ? "var(--bsp-fg)" : DC.grey,
          font: "400 14px/1 var(--bsp-font-sans)",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {triggerIcon ? (
          <span style={{ flex: "0 0 auto", color: DC.grey, display: "inline-flex" }}>
            {triggerIcon}
          </span>
        ) : null}
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? (
            <>
              {selected.label}
              {selected.sub ? (
                <span style={{ color: DC.grey, marginLeft: 6 }}>· {selected.sub}</span>
              ) : null}
            </>
          ) : (
            triggerLabel ?? placeholder
          )}
        </span>
        <ChevronDown size={14} color={DC.grey} />
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 70,
            background: DC.surface,
            border: `1px solid ${DC.borderStrong}`,
            borderRadius: 6,
            boxShadow: "var(--bsp-shadow-pop)",
            maxHeight: 280,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${DC.border}` }}>
            <input
              ref={filterRef}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                onSearchChange?.(e.target.value);
              }}
              placeholder={onSearchChange ? "Type 2+ characters to search…" : "Filter…"}
              style={{
                width: "100%",
                height: 30,
                padding: "0 8px",
                border: `1px solid ${DC.border}`,
                borderRadius: 4,
                outline: 0,
                font: "400 13px/1 var(--bsp-font-sans)",
              }}
            />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {allowClear && value !== null ? (
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                style={{
                  ...rowStyle,
                  color: DC.grey,
                  fontStyle: "italic",
                }}
              >
                <span style={{ width: 22 }} aria-hidden />
                Clear selection
              </button>
            ) : null}
            {loading ? (
              <div style={{ ...rowStyle, color: DC.grey }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ ...rowStyle, color: DC.grey }}>{emptyHint}</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                  }}
                  style={{
                    ...rowStyle,
                    background: o.id === value ? DC.primaryPale : "transparent",
                  }}
                >
                  {o.icon ? (
                    <span style={{ width: 22, display: "inline-flex", justifyContent: "center", color: DC.grey }}>
                      {o.icon}
                    </span>
                  ) : (
                    <span style={{ width: 22 }} aria-hidden />
                  )}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", color: "var(--bsp-fg)" }}>{o.label}</span>
                    {o.sub ? (
                      <span style={{ display: "block", fontSize: 11, color: DC.grey }}>{o.sub}</span>
                    ) : null}
                  </span>
                  {o.id === value ? <Check size={14} color={DC.primary} /> : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "8px 10px",
  border: 0,
  background: "transparent",
  font: "400 13px/1.3 var(--bsp-font-sans)",
  textAlign: "left",
  cursor: "pointer",
};

// ── PersonSelect ────────────────────────────────────────────────────
// Surfaces every tenant user via the server-side Graph-backed search
// (see services/team-planner/graphUsers.ts). Hits flagged
// `pendingProjection: true` are tenant users who don't yet have a
// planner_users row — on first pick we POST to /users/projection to
// upsert them, then swap the trigger value to the returned planner
// uuid so participant inserts FK to the new row.
export function PersonSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  // Server-driven search. Picker calls onSearchChange on every
  // keystroke; we debounce to 200ms so each character doesn't fire
  // a Graph round-trip. Empty query still returns the local
  // planner_users list so the dropdown isn't empty on first open.
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);
  const users = useResource(() => tpApi.searchUsers(debounced), [debounced]);
  const [resolved, setResolved] = useState<PlannerOwner | null>(null);
  const [projecting, setProjecting] = useState(false);

  useEffect(() => {
    if (!value || resolved?.id === value) return;
    const list = users.data ?? [];
    const hit = list.find((u) => u.id === value);
    if (hit) setResolved({ id: hit.id, displayName: hit.displayName, email: hit.email, entraObjectId: hit.entraObjectId });
  }, [value, users.data, resolved]);

  const options: PickerOption[] = (users.data ?? []).map((u) => ({
    id: u.id,
    label: u.displayName ?? u.email ?? "Unknown",
    sub: u.pendingProjection ? `${u.email ?? ""} · from directory` : u.email ?? null,
    icon: (
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          background: u.pendingProjection ? "var(--ink-200)" : "var(--bsp-blue-tint)",
          color: u.pendingProjection ? "var(--ink-700)" : "var(--bsp-blue)",
          font: "700 9px/1 var(--bsp-font-sans)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {initials(u.displayName)}
      </span>
    ),
  }));

  async function handlePick(id: string | null) {
    if (!id) {
      onChange(null);
      return;
    }
    const list = users.data ?? [];
    const hit = list.find((u) => u.id === id);
    if (hit?.pendingProjection && hit.entraObjectId) {
      // Graph hit — upsert into planner_users + Hub users first so the
      // caller can stamp the participant row with a valid uuid.
      setProjecting(true);
      try {
        const projected = await tpApi.projectUser({
          entraObjectId: hit.entraObjectId,
          displayName: hit.displayName,
          email: hit.email,
        });
        setResolved(projected);
        onChange(projected.id);
      } finally {
        setProjecting(false);
      }
      return;
    }
    onChange(id);
  }

  const selectedOption = options.find((o) => o.id === value);
  const selectedLabel = selectedOption?.label ?? resolved?.displayName ?? null;

  return (
    <Picker
      value={value}
      onChange={handlePick}
      options={options}
      placeholder={projecting ? "Adding to planner…" : "Pick someone…"}
      emptyHint={
        debounced.length === 0
          ? "Type a name or email to search the directory."
          : debounced.length < 2
            ? "Type at least 2 characters."
            : "No matches in the directory."
      }
      loading={users.loading || projecting}
      triggerIcon={<UserRound size={14} />}
      triggerLabel={selectedLabel ? <span>{selectedLabel}</span> : undefined}
      onSearchChange={setQuery}
    />
  );
}

// ── TeamSelect ──────────────────────────────────────────────────────
export function TeamSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const teams = useResource(() => tpApi.listTeams(), []);
  const options: PickerOption[] = (teams.data ?? []).map((t: TeamSummary) => ({
    id: t.id,
    label: t.name,
    sub: t.source === "department" ? "Department" : "Custom",
    icon: (
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 4,
          background: "var(--bsp-blue-tint)",
          color: "var(--bsp-blue)",
          font: "700 9px/1 var(--bsp-font-sans)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {(t.monogram ?? t.name.slice(0, 2)).toUpperCase()}
      </span>
    ),
  }));
  return (
    <Picker
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Pick a team…"
      loading={teams.loading}
      triggerIcon={<Users size={14} />}
    />
  );
}

// ── RockSelect ──────────────────────────────────────────────────────
export function RockSelect({
  value,
  onChange,
  quarter,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  /** Quarter context. When provided, the picker only surfaces rocks
   *  in that quarter (typical case: a new rock's milestone editor).
   *  Omit it to get every visible rock across quarters — used by the
   *  Milestone create form and the Todo "link to rock" field where
   *  the user hasn't picked a quarter context. */
  quarter?: string;
}) {
  const rocks = useResource(
    () => (quarter ? tpApi.listRocks(quarter, "all") : tpApi.listAllRocks()),
    [quarter],
  );
  const options: PickerOption[] = (rocks.data ?? []).map((r) => ({
    id: r.id,
    label: r.title,
    sub: `R${r.seq} · ${r.quarter}`,
    icon: <Target size={14} />,
  }));
  return (
    <Picker
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Unlinked — pick a Rock…"
      emptyHint={quarter ? "No rocks this quarter." : "No rocks yet."}
      loading={rocks.loading}
      triggerIcon={<Target size={14} />}
    />
  );
}

// ── MilestoneSelect ─────────────────────────────────────────────────
export function MilestoneSelect({
  value,
  onChange,
  rockId,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  rockId: string | null;
}) {
  const milestones = useResource(
    () => (rockId ? tpApi.listMilestones(rockId) : Promise.resolve([])),
    [rockId],
  );
  const options: PickerOption[] = (milestones.data ?? []).map((m) => ({
    id: m.id,
    label: m.title,
    sub: m.status,
    icon: <Flag size={14} />,
  }));
  return (
    <Picker
      value={value}
      onChange={onChange}
      options={options}
      placeholder={rockId ? "Pick a milestone…" : "Pick a Rock first"}
      emptyHint="No milestones."
      loading={milestones.loading}
      disabled={!rockId}
      triggerIcon={<Flag size={14} />}
    />
  );
}

// ── OpportunitySelect ───────────────────────────────────────────────
export function OpportunitySelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const issues = useResource(() => tpApi.listIssues("open"), []);
  const options: PickerOption[] = (issues.data ?? []).map((i) => ({
    id: i.id,
    label: i.title,
    sub: `O${i.seq} · ${i.voteCount} ${i.voteCount === 1 ? "vote" : "votes"}`,
    icon: <Lightbulb size={14} />,
  }));
  return (
    <Picker
      value={value}
      onChange={onChange}
      options={options}
      placeholder="None — not from an opportunity"
      loading={issues.loading}
      triggerIcon={<Lightbulb size={14} />}
    />
  );
}

// ── Generic SelectBtn (string options, used by quarter picker) ──────
export function SelectBtn({
  value,
  options,
  onChange,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <Picker
      value={value}
      onChange={(v) => v && onChange(v)}
      allowClear={false}
      options={options.map((v) => ({ id: v, label: v }))}
      placeholder="Pick…"
    />
  );
}
